<script setup lang="ts">
import { ref, watch, onMounted, defineEmits } from 'vue'
import { UIDropdownTree } from '@gohighlevel/ghl-ui'
import { Tag01Icon } from '@gohighlevel/ghl-icons/24/outline'
import { transformMenuOption } from 'common/utils/helper'
import { getTagOptions, getEmailOptions } from 'common/config'
const props = defineProps({
  id: { type: String, default: '' },
  class: { type: String, default: '' },
  noMarginTop: {
    type: Boolean,
    default: false,
  },
  outsideField: {
    type: Boolean,
    default: false,
  },
  customFieldsLocation: {
    type: Array as PropType<Array<ICustomField>>,
    default: [],
  },
  customValuesLocation: {
    type: Array as PropType<Array<ICustomValue>>,
    default: [],
  },
  triggerLinks: {
    type: Array as PropType<Array<ITriggerLinks>>,
    default: [],
  },
  emailTagsOnly: {
    type: Boolean,
    default: false,
  },
})
const customValues = ref(transformMenuOption(getTagOptions()))
const emit = defineEmits(['update:selectedValue'])
const handleSelect = (value: string) => {
  emit('update:selectedValue', { value })
}
onMounted(() => {
  let options = transformMenuOption(
    props.emailTagsOnly ? getEmailOptions() : getTagOptions()
  )
  if (props.customValuesLocation?.length) {
    options = [
      ...options,
      {
        label: 'Custom Values',
        value: 'Custom Values',
        children: props.customValuesLocation,
      },
    ]
  }
  if (props.customFieldsLocation?.length) {
    const contactIndex = options.findIndex(
      val => val.label.toLowerCase() === 'contact'
    )
    if (contactIndex > -1) {
      options[contactIndex].children = [
        ...options[contactIndex].children,
        {
          label: 'Custom Fields',
          value: 'Custom Fields',
          children: props.customFieldsLocation.map(({ name, fieldKey }) => ({
            label: name,
            value: `{{${fieldKey}}}`,
          })),
        },
      ]
    }
  }

  if (props?.triggerLinks?.length) {
    options = [
      ...options,
      {
        label: 'Trigger Links',
        value: 'Trigger Links',
        children: props.triggerLinks.map(link => ({
          label: link.name,
          value: `{{trigger_link.${link.id}}}`,
        })),
      },
    ]
  }
  customValues.value = options
})
</script>

<template>
  <div>
    <UIDropdownTree
      :options="customValues"
      @onSelect="handleSelect"
      placement="bottom-end"
      :id="`custom-value-dropdown${id}`"
    >
      <template #trigger
        ><div
          class="flex items-center cursor-pointer"
          :class="[
            noMarginTop ? 'mt-0' : 'mt-5',
            outsideField ? 'relative' : 'absolute inset-y-0 right-0 pr-3 pl-2',
          ]"
        >
          <Tag01Icon class="h-5 w-5" />
        </div>
      </template>
    </UIDropdownTree>
  </div>
</template>
<style lang="css">
.dropdown-tree__container {
  max-height: 24rem !important;
}
.v-binder-follower-content:has(#custom-value-dropdown__from-email-custom) {
  transform: translateX(calc(100vw - 25px)) translateY(206px) translateX(-100%) !important;
  transform-origin: right top !important;
  margin-top: 6px !important;
}
.v-binder-follower-content:has(#custom-value-dropdown__to-email-custom) {
  transform: translateX(calc(100vw - 25px)) translateY(295px) translateX(-100%) !important;
  transform-origin: right top !important;
  margin-top: 6px !important;
}
.v-binder-follower-content:has(#custom-value-dropdown__email-subject-custom) {
  transform: translateX(calc(100vw - 25px)) translateY(492px) translateX(-100%) !important;
  transform-origin: right top !important;
  margin-top: 6px !important;
}
.v-binder-follower-content:has(#custom-value-dropdown__template-setting-from-name) {
  transform: translateX(calc(100vw - 25px)) translateY(206px) translateX(-100%) !important;
  transform-origin: right top !important;
  margin-top: 6px !important;
}
.v-binder-follower-content:has(#custom-value-dropdown__template-setting-from-email) {
  transform: translateX(calc(100vw - 25px)) translateY(310px) translateX(-100%) !important;
  transform-origin: right top !important;
  margin-top: 6px !important;
}
.v-binder-follower-content:has(#custom-value-dropdown__template-setting-preview-text) {
  transform: translateX(calc(100vw - 25px)) translateY(430px) translateX(-100%) !important;
  transform-origin: right top !important;
  margin-top: 6px !important;
}
.v-binder-follower-content:has(#custom-value-dropdown__template-setting-email-subject) {
  transform: translateX(calc(100vw - 25px)) translateY(115px) translateX(-100%) !important;
  transform-origin: right top !important;
  margin-top: 6px !important;
}
.dropdown-tree__container {
  font-family: Inter;
}
</style>
